import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm, UseFormProps } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router';
import { Observable, isObservable } from 'rxjs';
import { take, finalize } from 'rxjs/operators';

export interface ModalStackPropsBase<SubmitValue, CloseValue> {
  show?: boolean;
  onSubmit?: (val: SubmitValue) => void | Observable<any>;
  onClose?: (val: CloseValue) => void;
}

interface ModalStackBase<
  SubmitValue,
  CloseValue,
  ModalStackProps extends ModalStackPropsBase<SubmitValue, CloseValue>,
> extends React.FC<ModalStackProps> {}

interface ModalStackItem {
  state: { [key: string]: any };
  form: { [key: string]: { props: any; values: any; isSubmitted?: any } };
  element: JSX.Element;
  onClose?: (dismissAll: () => void) => void;
}

const ModalStackContextDataDefault = (
  setModalStack?: React.Dispatch<React.SetStateAction<ModalStackItem[]>>,
  modalStack: ModalStackItem[] = [],
) => {
  const dismissAll = () => {
    setModalStack && setModalStack([]);
  };

  const popModal = () => {
    setModalStack &&
      setModalStack((currModalStack) => {
        if (currModalStack.length > 0) {
          const currModal = currModalStack[currModalStack.length - 1];
          currModal && currModal.onClose && currModal.onClose(dismissAll);
        }
        return currModalStack.slice(0, currModalStack.length - 1);
      });
  };

  function renderModal<P extends ModalStackPropsBase<any, any>>(
    ModalComponent: React.FC<P>,
    modalProps: (React.Attributes & P) | any,
  ) {
    if (popModal) {
      return modalProps ? (
        <ModalComponent
          {...modalProps}
          show={true}
          onClose={(val) => {
            modalProps.onClose && modalProps.onClose(val);
            popModal();
          }}
          onSubmit={
            modalProps.onSubmit
              ? isObservable(modalProps.onSubmit)
                ? (modalProps.onSubmit as Observable<any>).pipe(
                    take(1),
                    finalize(() => popModal()),
                  )
                : (val) => {
                    modalProps.onSubmit && modalProps.onSubmit(val);
                    popModal();
                  }
              : () => popModal()
          }
        />
      ) : (
        <ModalComponent
          {...modalProps}
          show={true}
          onClose={(val) => {
            popModal();
          }}
          onSubmit={(val) => {
            popModal();
          }}
        />
      );
    }
  }

  return {
    push: function <P extends ModalStackPropsBase<any, any>>(
      ModalComponent: React.FunctionComponent<P>,
      modalProps: React.Attributes & P,
      onClose?: ModalStackItem['onClose'],
    ) {
      if (setModalStack) {
        const renderedModal = renderModal(ModalComponent, modalProps);
        renderedModal &&
          setModalStack((curr) => {
            return [...curr, { element: renderedModal, state: {}, onClose, form: {} }];
          });
      }
    },
    pop: () => {
      setModalStack &&
        setModalStack((curr) => {
          const lastIndex = curr.length;
          return [...curr.slice(0, lastIndex - 1)];
        });
    },
    dismissAll,
    useModalMemory: function <S>(key: string, init?: S): [S, Dispatch<SetStateAction<S>>] {
      const currModal = modalStack[modalStack.length - 1] || { state: {} };
      if (currModal.state[key] === undefined) {
        currModal.state[key] = init;
      }
      const [state, setState] = useState(currModal.state[key]);
      return [
        state,
        (val: S | ((prevState: S) => S)) => {
          if (typeof val === 'function') {
            const fnVal = val as (prevState: S) => S;
            val = fnVal(currModal.state[key]);
          }
          currModal.state[key] = val;
          setState(val);
        },
      ];
    },
    useModalForm: function <
      TFieldValues extends FieldValues = FieldValues,
      TContext extends object = object,
    >(key: string, props: UseFormProps<TFieldValues, TContext>) {
      const currModal = modalStack[modalStack.length - 1] || { form: {} };
      if (currModal.form[key] === undefined) {
        currModal.form[key] = { props, values: props.defaultValues };
      }
      const currFormStage = useForm<TFieldValues, TContext>({
        ...currModal.form[key].props,
        defaultValues: JSON.parse(JSON.stringify(currModal.form[key].values)),
      });
      useEffect(() => {
        currModal.form[key].isSubmitted && currFormStage.trigger();
        currFormStage.formState.isSubmitted = currModal.form[key].isSubmitted;
        return () => {
          currModal.form[key] = {
            props,
            values: currFormStage.getValues(),
            isSubmitted: currModal.form[key].isSubmitted || currFormStage.formState.isSubmitted,
          };
        };
      }, []);
      return currFormStage;
    },
  };
};

export type ModalStackContextData = ReturnType<typeof ModalStackContextDataDefault>;

const ModalStackContext = React.createContext<ModalStackContextData>(
  ModalStackContextDataDefault(),
);
const useModalStack = () => useContext(ModalStackContext);
const ModalStackProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const [modalStack, setModalStack] = useState<ModalStackItem[]>([]);
  const [currentModal, setCurrentModal] = useState<JSX.Element>();
  const ModalStackContextData = useMemo(
    () => ModalStackContextDataDefault(setModalStack, modalStack),
    [modalStack],
  );
  const location = useLocation();

  useEffect(() => {
    setCurrentModal(modalStack[modalStack.length - 1]?.element);
  }, [modalStack]);

  useEffect(() => {
    return () => {
      setModalStack([]);
    };
  }, [location.pathname]);

  return (
    <ModalStackContext.Provider value={ModalStackContextData}>
      {children}
      {currentModal}
    </ModalStackContext.Provider>
  );
};

export { ModalStackProvider, useModalStack };
